import React, { useState } from "react";
import { Copy, Check, Play, Trophy, Shield, Crosshair, Users, ArrowRight, ExternalLink, Zap } from "lucide-react";
import { toast } from "sonner";
import { Avatar, Username } from "../UserBadge";
import api from "../../lib/api";

export default function MatchLobbyView({ match, user, onBack }) {
  const [copied, setCopied] = useState(false);
  const [simulating, setSimulating] = useState(false);

  if (!match) return null;

  const connectCmd = `connect ${match.server_ip}:${match.server_port}; password ${match.server_password}`;
  const steamUri = `steam://connect/${match.server_ip}:${match.server_port}/${match.server_password}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(connectCmd);
    setCopied(true);
    toast.success("Comanda de conectare a fost copiată în clipboard!");
    setTimeout(() => setCopied(false), 2500);
  };

  const handleConnectSteam = () => {
    window.location.href = steamUri;
  };

  const handleSimulateRound = async () => {
    try {
      setSimulating(true);
      await api.post(`/arena/matches/${match.id}/simulate-round`);
      toast.success("Rundă simulată cu succes!");
    } catch (e) {
      toast.error("Eroare la simulare.");
    } finally {
      setSimulating(false);
    }
  };

  const isCompleted = match.status === "COMPLETED";

  return (
    <div className="space-y-6">
      {/* Top Header Match Summary */}
      <div className="p-6 rounded-3xl bg-[#0b0e14] border border-white/10 shadow-2xl relative overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${isCompleted ? "bg-zinc-800 text-zinc-400" : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 animate-pulse"}`}>
                {isCompleted ? "MECI ÎNCHEIAT" : "MECI ÎN DESFĂȘURARE"}
              </span>
              <span className="text-xs font-mono text-zinc-400 font-bold uppercase">{match.mode} ARENA • HARTA: <strong className="text-white">{match.map}</strong></span>
            </div>
            <h2 className="text-2xl font-black text-white uppercase mt-2 tracking-wide">
              {match.team1_name} vs {match.team2_name}
            </h2>
          </div>

          {/* Big Score Display */}
          <div className="flex items-center gap-6 px-6 py-3 rounded-2xl bg-black/60 border border-white/10">
            <div className="text-center">
              <span className="text-[10px] font-black uppercase text-sky-400 block">CT TEAM</span>
              <span className="text-3xl font-black text-sky-400">{match.score_ct || 0}</span>
            </div>
            <span className="text-2xl font-black text-zinc-600">:</span>
            <div className="text-center">
              <span className="text-[10px] font-black uppercase text-amber-500 block">T TEAM</span>
              <span className="text-3xl font-black text-amber-500">{match.score_t || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* QUICK CONNECT BAR */}
      {!isCompleted && (
        <div className="p-6 rounded-3xl bg-gradient-to-r from-orange-500/10 via-black to-orange-500/10 border border-orange-500/40 shadow-xl">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex-1 min-w-[280px]">
              <span className="text-[10px] font-black uppercase tracking-widest text-orange-400 block mb-1">
                🎮 CONECTARE SERVER DEDICAT CS2
              </span>
              <div className="flex items-center gap-2 p-2.5 rounded-xl bg-black/80 border border-white/10 font-mono text-xs text-zinc-300 select-all">
                <span className="text-zinc-500">console:</span>
                <span className="text-orange-300 font-bold truncate">{connectCmd}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleCopy}
                className="px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase flex items-center gap-2 transition-all"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copiat!" : "Copiază Comanda"}
              </button>

              <button
                onClick={handleConnectSteam}
                className="px-6 py-3 rounded-xl bg-[var(--tf-primary)] hover:bg-orange-400 text-black font-black text-xs uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-orange-500/20 transition-all hover:scale-105"
              >
                <Play className="w-4 h-4 fill-current" />
                Lansează în CS2
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TEAMS ROSTERS & SCOREBOARD */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* TEAM 1 (CT) */}
        <div className="p-6 rounded-3xl bg-[#080a0f] border border-sky-500/30 shadow-xl">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-sky-500/20">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-sky-400" />
              <h3 className="text-base font-black text-sky-400 uppercase tracking-wider">{match.team1_name} (CT)</h3>
            </div>
            <span className="text-xs font-mono font-bold text-sky-300">{match.score_ct || 0} Runde</span>
          </div>

          <div className="space-y-3">
            {match.team1?.map((p) => {
              const st = match.stats?.[p.id] || { kills: 0, deaths: 0, assists: 0, mvps: 0 };
              return (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-xl bg-black/40 border border-white/5 hover:border-white/10 transition-all">
                  <div className="flex items-center gap-3">
                    <Avatar user={p} size={36} hover={true} />
                    <div>
                      <Username user={p} className="font-bold text-xs" />
                      <span className="text-[10px] text-zinc-500 block font-mono">{p.rank || "Global Elite"} • {p.elo || 1200} ELO</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs font-mono">
                    <span className="text-zinc-400"><strong>{st.kills}</strong> K</span>
                    <span className="text-zinc-500"><strong>{st.deaths}</strong> D</span>
                    {st.mvps > 0 && <span className="text-amber-400 font-bold">★ {st.mvps}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* TEAM 2 (T) */}
        <div className="p-6 rounded-3xl bg-[#080a0f] border border-amber-500/30 shadow-xl">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-amber-500/20">
            <div className="flex items-center gap-2">
              <Crosshair className="w-5 h-5 text-amber-500" />
              <h3 className="text-base font-black text-amber-500 uppercase tracking-wider">{match.team2_name} (T)</h3>
            </div>
            <span className="text-xs font-mono font-bold text-amber-400">{match.score_t || 0} Runde</span>
          </div>

          <div className="space-y-3">
            {match.team2?.map((p) => {
              const st = match.stats?.[p.id] || { kills: 0, deaths: 0, assists: 0, mvps: 0 };
              return (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-xl bg-black/40 border border-white/5 hover:border-white/10 transition-all">
                  <div className="flex items-center gap-3">
                    <Avatar user={p} size={36} hover={true} />
                    <div>
                      <Username user={p} className="font-bold text-xs" />
                      <span className="text-[10px] text-zinc-500 block font-mono">{p.rank || "Global Elite"} • {p.elo || 1200} ELO</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs font-mono">
                    <span className="text-zinc-400"><strong>{st.kills}</strong> K</span>
                    <span className="text-zinc-500"><strong>{st.deaths}</strong> D</span>
                    {st.mvps > 0 && <span className="text-amber-400 font-bold">★ {st.mvps}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Admin / Testing Tools */}
      {!isCompleted && (
        <div className="p-4 rounded-2xl bg-black/40 border border-white/5 flex items-center justify-between">
          <span className="text-xs text-zinc-500 font-mono">Panou Test Meci Live (Orchestrator Simulation)</span>
          <button
            onClick={handleSimulateRound}
            disabled={simulating}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-bold text-zinc-300 flex items-center gap-2 transition-all"
          >
            <Zap className="w-3.5 h-3.5 text-amber-400" />
            Simulează Următoarea Rundă
          </button>
        </div>
      )}
    </div>
  );
}
